import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create a configured axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Request interceptor to automatically attach authorization token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token expiration/unauthorized errors
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
            // Clear storage and redirect/reload if auth context is not handling
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

const isDemo = () => sessionStorage.getItem('isDemoMode') === 'true';

const generateMockScreenBlob = (machineName) => {
    if (typeof document === 'undefined') return new Blob();
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 768;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new Blob();

    // Draw dark background desktop wallpaper
    const grad = ctx.createRadialGradient(512, 384, 50, 512, 384, 600);
    grad.addColorStop(0, '#1e293b');
    grad.addColorStop(1, '#0f172a');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1024, 768);

    // Draw background grid lines (subtle matrix style)
    ctx.strokeStyle = 'rgba(99, 102, 241, 0.05)';
    ctx.lineWidth = 1;
    for (let x = 0; x < 1024; x += 40) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, 768);
        ctx.stroke();
    }
    for (let y = 0; y < 768; y += 40) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(1024, y);
        ctx.stroke();
    }

    // Draw active program window (Code Editor)
    ctx.fillStyle = '#1e1e24';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 20;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(100, 100, 550, 400, 10); else ctx.rect(100, 100, 550, 400);
    ctx.fill();
    ctx.shadowBlur = 0; // reset shadow

    // Title bar
    ctx.fillStyle = '#2d2d34';
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(100, 100, 550, 35, [10, 10, 0, 0]); else ctx.rect(100, 100, 550, 35);
    ctx.fill();

    // Title bar window controls
    ctx.fillStyle = '#ff5f56'; // red
    ctx.beginPath(); ctx.arc(125, 117, 6, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#ffbd2e'; // yellow
    ctx.beginPath(); ctx.arc(145, 117, 6, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#27c93f'; // green
    ctx.beginPath(); ctx.arc(165, 117, 6, 0, Math.PI * 2); ctx.fill();

    // Title text
    ctx.fillStyle = '#94a3b8';
    ctx.font = 'bold 12px Consolas, monospace';
    ctx.fillText(`VS Code - LANternService.cs - [${machineName}]`, 190, 122);

    // Code lines
    ctx.font = '13px Consolas, monospace';
    const lines = [
        'using System;',
        'using System.Net.Sockets;',
        '',
        'namespace LANtern {',
        '    class RemoteMirrorService {',
        '        static void Main(string[] args) {',
        '            var server = new TcpListener(49191);',
        '            server.Start();',
        '            Console.WriteLine("Loopback active...");',
        '            // Stream screenshot payload dynamically',
        `            var target = "${machineName}";`,
        '            while(true) {',
        `                var frame = CaptureDesktop();`,
        '                StreamFrame(frame);',
        '            }',
        '        }',
        '    }',
        '}'
    ];
    let yPos = 160;
    lines.forEach((line) => {
        if (line.includes('using') || line.includes('namespace') || line.includes('class') || line.includes('void') || line.includes('var') || line.includes('new') || line.includes('while')) {
            ctx.fillStyle = '#569cd6';
        } else if (line.includes('"')) {
            ctx.fillStyle = '#ce9178';
        } else if (line.startsWith('//') || line.includes('//')) {
            ctx.fillStyle = '#6a9955';
        } else {
            ctx.fillStyle = '#d4d4d4';
        }
        ctx.fillText(line, 130, yPos);
        yPos += 20;
    });

    // Draw another window (System Status dashboard)
    ctx.fillStyle = '#0f172a';
    ctx.strokeStyle = 'rgba(99, 102, 241, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(500, 350, 450, 300, 8); else ctx.rect(500, 350, 450, 300);
    ctx.fill();
    ctx.stroke();

    // Dashboard header
    ctx.fillStyle = 'rgba(99, 102, 241, 0.1)';
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(500, 350, 450, 40, [8, 8, 0, 0]); else ctx.rect(500, 350, 450, 40);
    ctx.fill();

    ctx.fillStyle = '#e2e8f0';
    ctx.font = 'bold 13px sans-serif';
    ctx.fillText('LANtern Service Diagnostics', 520, 375);

    // Performance graph
    ctx.fillStyle = 'rgba(16, 185, 129, 0.1)';
    ctx.fillRect(520, 410, 410, 150);
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(520, 530);
    for (let i = 0; i <= 410; i += 10) {
        const randY = 480 + Math.sin((i + Date.now() / 200) * 0.05) * 30 + Math.cos(i * 0.1) * 10;
        ctx.lineTo(520 + i, randY);
    }
    ctx.stroke();

    ctx.fillStyle = '#10b981';
    ctx.font = '11px sans-serif';
    ctx.fillText('Agent CPU Usage: 4.8%  |  Network Send Rate: 2.1 MB/s', 525, 585);

    // Taskbar at the bottom
    ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
    ctx.fillRect(0, 728, 1024, 40);

    // Start Button
    ctx.fillStyle = '#6366f1';
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(10, 733, 80, 30, 4); else ctx.rect(10, 733, 80, 30);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 11px sans-serif';
    ctx.fillText('LANtern', 30, 752);

    // Clock
    ctx.fillStyle = '#94a3b8';
    ctx.font = '12px sans-serif';
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    ctx.fillText(timeStr, 940, 752);

    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(blob);
        }, 'image/jpeg', 0.8);
    });
};

const initMockStorage = () => {
    if (!localStorage.getItem('lantern_mock_machines')) {
        const defaultMachines = [
            {
                id: 'mock-mac-01',
                name: 'Workstation-01',
                mac_address: '00:1A:2B:3C:4D:5E',
                ip_address: '192.168.1.10',
                subnet_mask: '255.255.255.0',
                broadcast_address: '192.168.1.255',
                connection_status: 'connected',
                is_active: 1
            },
            {
                id: 'mock-mac-02',
                name: 'Design-Rig-02',
                mac_address: '00:1A:2B:3C:4D:5F',
                ip_address: '192.168.1.11',
                subnet_mask: '255.255.255.0',
                broadcast_address: '192.168.1.255',
                connection_status: 'connected',
                is_active: 1
            },
            {
                id: 'mock-mac-03',
                name: 'Server-Room-03',
                mac_address: '00:1A:2B:3C:4D:60',
                ip_address: '192.168.1.12',
                subnet_mask: '255.255.255.0',
                broadcast_address: '192.168.1.255',
                connection_status: 'disconnected',
                is_active: 0
            },
            {
                id: 'mock-mac-04',
                name: 'Finance-PC-04',
                mac_address: '00:1A:2B:3C:4D:61',
                ip_address: '192.168.1.13',
                subnet_mask: '255.255.255.0',
                broadcast_address: '192.168.1.255',
                connection_status: 'connected',
                is_active: 1
            }
        ];
        localStorage.setItem('lantern_mock_machines', JSON.stringify(defaultMachines));
    }

    if (!localStorage.getItem('lantern_mock_clusters')) {
        const defaultClusters = [
            {
                id: 'mock-clus-01',
                name: 'Development Lab',
                description: 'Workstations situated inside main room',
                machine_count: 2,
                machines: ['mock-mac-01', 'mock-mac-02']
            },
            {
                id: 'mock-clus-02',
                name: 'Critical Infrastructure',
                description: 'Sensitive backend servers and monitoring nodes',
                machine_count: 1,
                machines: ['mock-mac-03']
            }
        ];
        localStorage.setItem('lantern_mock_clusters', JSON.stringify(defaultClusters));
    }

    if (!localStorage.getItem('lantern_mock_logs_power')) {
        const defaultPowerLogs = [
            { id: 'log-01', machine_id: 'mock-mac-01', action: 'kiosk_start', status: 'success', initiated_by: 'demo_admin', timestamp: new Date(Date.now() - 3600000).toISOString() },
            { id: 'log-02', machine_id: 'mock-mac-02', action: 'wake', status: 'success', initiated_by: 'demo_admin', timestamp: new Date(Date.now() - 7200000).toISOString() }
        ];
        localStorage.setItem('lantern_mock_logs_power', JSON.stringify(defaultPowerLogs));
    }

    if (!localStorage.getItem('lantern_mock_logs_system')) {
        const defaultSystemLogs = [
            { id: 'sys-01', module: 'AGENT', level: 'INFO', message: 'Local TCP Loopback listening on port 49191', timestamp: new Date(Date.now() - 3600000).toISOString() },
            { id: 'sys-02', module: 'WEBSOCKET', level: 'INFO', message: 'WebSocket tunnel established successfully', timestamp: new Date(Date.now() - 7200000).toISOString() }
        ];
        localStorage.setItem('lantern_mock_logs_system', JSON.stringify(defaultSystemLogs));
    }

    if (!localStorage.getItem('lantern_mock_users')) {
        const defaultUsers = [
            { id: 'user-01', username: 'demo_admin', role: 'admin', permissions: [], last_login: new Date().toISOString() },
            { id: 'user-02', username: 'operator', role: 'user', permissions: ['power', 'remote_view'], last_login: new Date(Date.now() - 86400000).toISOString() }
        ];
        localStorage.setItem('lantern_mock_users', JSON.stringify(defaultUsers));
    }
};

const getMockMachines = () => JSON.parse(localStorage.getItem('lantern_mock_machines') || '[]');
const saveMockMachines = (data) => localStorage.setItem('lantern_mock_machines', JSON.stringify(data));

const getMockClusters = () => JSON.parse(localStorage.getItem('lantern_mock_clusters') || '[]');
const saveMockClusters = (data) => localStorage.setItem('lantern_mock_clusters', JSON.stringify(data));

const getMockPowerLogs = () => JSON.parse(localStorage.getItem('lantern_mock_logs_power') || '[]');
const saveMockPowerLogs = (data) => localStorage.setItem('lantern_mock_logs_power', JSON.stringify(data));

const getMockSystemLogs = () => JSON.parse(localStorage.getItem('lantern_mock_logs_system') || '[]');
const saveMockSystemLogs = (data) => localStorage.setItem('lantern_mock_logs_system', JSON.stringify(data));

const getMockUsers = () => JSON.parse(localStorage.getItem('lantern_mock_users') || '[]');
const saveMockUsers = (data) => localStorage.setItem('lantern_mock_users', JSON.stringify(data));

const addMockLog = (type, targetIdOrModule, actionOrLevel, statusOrMessage) => {
    if (type === 'power') {
        const logs = getMockPowerLogs();
        const list = getMockMachines();
        const machine = list.find(m => m.id === targetIdOrModule);
        logs.unshift({
            id: `log-${Date.now()}-${Math.random()}`,
            machine_id: targetIdOrModule,
            machine: machine ? { name: machine.name } : null,
            action: actionOrLevel,
            status: statusOrMessage,
            initiated_by: 'demo_admin',
            timestamp: new Date().toISOString()
        });
        saveMockPowerLogs(logs);
    } else {
        const logs = getMockSystemLogs();
        logs.unshift({
            id: `sys-${Date.now()}-${Math.random()}`,
            module: targetIdOrModule,
            level: actionOrLevel,
            message: statusOrMessage,
            timestamp: new Date().toISOString()
        });
        saveMockSystemLogs(logs);
    }
};

const mockMachineService = {
    getAll: async () => {
        initMockStorage();
        return getMockMachines();
    },
    create: async (data) => {
        const list = getMockMachines();
        const newMachine = {
            id: `mock-mac-${Date.now()}`,
            connection_status: 'disconnected',
            is_active: 0,
            ...data
        };
        list.push(newMachine);
        saveMockMachines(list);
        addMockLog('system', 'ASSET', 'INFO', `Registered new network machine: ${data.name}`);
        return newMachine;
    },
    update: async (id, data) => {
        const list = getMockMachines();
        const index = list.findIndex(m => m.id === id);
        if (index !== -1) {
            list[index] = { ...list[index], ...data };
            saveMockMachines(list);
            addMockLog('system', 'ASSET', 'INFO', `Updated configurations for machine: ${list[index].name}`);
            return list[index];
        }
        throw new Error('Machine not found');
    },
    updateStatus: async (id, is_active) => {
        const list = getMockMachines();
        const index = list.findIndex(m => m.id === id);
        if (index !== -1) {
            list[index].is_active = is_active;
            saveMockMachines(list);
            return list[index];
        }
        throw new Error('Machine not found');
    },
    delete: async (id) => {
        let list = getMockMachines();
        const item = list.find(m => m.id === id);
        list = list.filter(m => m.id !== id);
        saveMockMachines(list);
        if (item) {
            addMockLog('system', 'ASSET', 'WARNING', `Removed machine asset: ${item.name}`);
        }
        return { success: true };
    },
    reveal: async (id, password) => {
        const list = getMockMachines();
        const machine = list.find(m => m.id === id);
        if (!machine) throw new Error('Machine not found');
        return {
            ip_address: machine.ip_address,
            mac_address: machine.mac_address
        };
    },
    wake: async (id) => {
        const list = getMockMachines();
        const index = list.findIndex(m => m.id === id);
        if (index === -1) throw new Error('Machine not found');
        list[index].is_active = 1;
        list[index].connection_status = 'connected';
        saveMockMachines(list);
        addMockLog('power', id, 'wake', 'success');
        return { success: true };
    },
    shutdown: async (id, password) => {
        const list = getMockMachines();
        const index = list.findIndex(m => m.id === id);
        if (index === -1) throw new Error('Machine not found');
        list[index].is_active = 0;
        list[index].connection_status = 'disconnected';
        saveMockMachines(list);
        addMockLog('power', id, 'shutdown', 'success');
        return { success: true };
    },
    restart: async (id, password) => {
        const list = getMockMachines();
        const index = list.findIndex(m => m.id === id);
        if (index === -1) throw new Error('Machine not found');
        list[index].is_active = 1;
        saveMockMachines(list);
        addMockLog('power', id, 'restart', 'success');
        return { success: true };
    },
    getScreenshot: async (id) => {
        const list = getMockMachines();
        const machine = list.find(m => m.id === id);
        const name = machine ? machine.name : 'Unknown';
        return generateMockScreenBlob(name);
    },
    ping: async (id) => {
        const list = getMockMachines();
        const machine = list.find(m => m.id === id);
        if (!machine) throw new Error('Machine not found');
        return { success: true, message: `Ping request responded in 12ms` };
    },
    clusterAction: async (machineIds, action) => {
        const list = getMockMachines();
        machineIds.forEach(id => {
            const index = list.findIndex(m => m.id === id);
            if (index !== -1) {
                if (action === 'wake') {
                    list[index].is_active = 1;
                    list[index].connection_status = 'connected';
                } else if (action === 'shutdown') {
                    list[index].is_active = 0;
                    list[index].connection_status = 'disconnected';
                } else if (action === 'restart') {
                    list[index].is_active = 1;
                }
                addMockLog('power', id, action, 'success');
            }
        });
        saveMockMachines(list);
        return { success: true };
    },
    startKiosk: async (id, url) => {
        addMockLog('power', id, 'kiosk_start', 'success');
        return { success: true };
    },
    stopKiosk: async (id) => {
        addMockLog('power', id, 'kiosk_stop', 'success');
        return { success: true };
    },
    bulkKiosk: async (machineIds, action, url) => {
        machineIds.forEach(id => {
            addMockLog('power', id, `kiosk_${action}`, 'success');
        });
        return { success: true };
    }
};

const mockClusterService = {
    getAll: async () => {
        initMockStorage();
        return getMockClusters();
    },
    getById: async (id) => {
        const clusters = getMockClusters();
        const cluster = clusters.find(c => c.id === id);
        if (!cluster) throw new Error('Cluster not found');
        const machines = getMockMachines();
        const populatedMachines = machines.filter(m => cluster.machines.includes(m.id));
        return {
            ...cluster,
            machines: populatedMachines
        };
    },
    create: async (data) => {
        const list = getMockClusters();
        const newCluster = {
            id: `mock-clus-${Date.now()}`,
            machine_count: data.machineIds?.length || 0,
            machines: data.machineIds || [],
            ...data
        };
        list.push(newCluster);
        saveMockClusters(list);
        addMockLog('system', 'CLUSTER', 'INFO', `Created new cluster group: ${data.name}`);
        return newCluster;
    },
    update: async (id, data) => {
        const list = getMockClusters();
        const index = list.findIndex(c => c.id === id);
        if (index !== -1) {
            list[index] = { 
                ...list[index], 
                ...data,
                machines: data.machineIds || list[index].machines,
                machine_count: data.machineIds ? data.machineIds.length : list[index].machine_count
            };
            saveMockClusters(list);
            addMockLog('system', 'CLUSTER', 'INFO', `Modified cluster configuration: ${list[index].name}`);
            return list[index];
        }
        throw new Error('Cluster not found');
    },
    delete: async (id) => {
        let list = getMockClusters();
        const item = list.find(c => c.id === id);
        list = list.filter(c => c.id !== id);
        saveMockClusters(list);
        if (item) {
            addMockLog('system', 'CLUSTER', 'WARNING', `Deleted cluster group: ${item.name}`);
        }
        return { success: true };
    },
    performAction: async (id, action) => {
        const cluster = getMockClusters().find(c => c.id === id);
        if (!cluster) throw new Error('Cluster not found');
        await mockMachineService.clusterAction(cluster.machines, action);
        return { success: true };
    }
};

const mockLogService = {
    getPowerLogs: async () => {
        initMockStorage();
        return getMockPowerLogs();
    },
    getSystemLogs: async () => {
        initMockStorage();
        return getMockSystemLogs();
    }
};

const mockUserService = {
    getAll: async () => {
        initMockStorage();
        return getMockUsers();
    },
    create: async (data) => {
        const list = getMockUsers();
        const newUser = {
            id: `mock-user-${Date.now()}`,
            last_login: null,
            ...data
        };
        list.push(newUser);
        saveMockUsers(list);
        addMockLog('system', 'SECURITY', 'INFO', `Created new user login account: ${data.username}`);
        return newUser;
    },
    update: async (id, data) => {
        const list = getMockUsers();
        const index = list.findIndex(u => u.id === id);
        if (index !== -1) {
            list[index] = { ...list[index], ...data };
            saveMockUsers(list);
            addMockLog('system', 'SECURITY', 'INFO', `Modified credentials policies for: ${list[index].username}`);
            return list[index];
        }
        throw new Error('User not found');
    },
    delete: async (id) => {
        let list = getMockUsers();
        const item = list.find(u => u.id === id);
        list = list.filter(u => u.id !== id);
        saveMockUsers(list);
        if (item) {
            addMockLog('system', 'SECURITY', 'WARNING', `Removed user account: ${item.username}`);
        }
        return { success: true };
    }
};

export const authService = {
    login: async (credentials) => {
        if (isDemo()) {
            return {
                message: 'Login successful',
                token: 'mock-jwt-token-for-demo',
                user: {
                    id: 'mock-user-01',
                    username: 'demo_admin',
                    role: 'admin',
                    permissions: []
                }
            };
        }
        const response = await apiClient.post('/auth/login', credentials);
        return response.data;
    },
    logout: async () => {
        if (isDemo()) {
            return { message: 'Logout successful' };
        }
        const response = await apiClient.post('/auth/logout');
        return response.data;
    },
    verify: async (token) => {
        if (isDemo()) {
            return {
                valid: true,
                user: {
                    id: 'mock-user-01',
                    username: 'demo_admin',
                    role: 'admin',
                    permissions: []
                }
            };
        }
        const response = await apiClient.get('/auth/verify', {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    }
};

export const machineService = {
    getAll: async () => {
        if (isDemo()) return mockMachineService.getAll();
        const response = await apiClient.get('/machines');
        return response.data;
    },
    create: async (machineData) => {
        if (isDemo()) return mockMachineService.create(machineData);
        const response = await apiClient.post('/machines', machineData);
        return response.data;
    },
    update: async (id, machineData) => {
        if (isDemo()) return mockMachineService.update(id, machineData);
        const response = await apiClient.put(`/machines/${id}`, machineData);
        return response.data;
    },
    updateStatus: async (id, is_active) => {
        if (isDemo()) return mockMachineService.updateStatus(id, is_active);
        const response = await apiClient.patch(`/machines/${id}/status`, { is_active });
        return response.data;
    },
    delete: async (id) => {
        if (isDemo()) return mockMachineService.delete(id);
        const response = await apiClient.delete(`/machines/${id}`);
        return response.data;
    },
    reveal: async (id, password) => {
        if (isDemo()) return mockMachineService.reveal(id, password);
        const response = await apiClient.post(`/machines/${id}/reveal`, { password });
        return response.data;
    },
    wake: async (id) => {
        if (isDemo()) return mockMachineService.wake(id);
        const response = await apiClient.post(`/machines/${id}/wake`);
        return response.data;
    },
    shutdown: async (id, password) => {
        if (isDemo()) return mockMachineService.shutdown(id, password);
        const response = await apiClient.post(`/machines/${id}/shutdown`, { password });
        return response.data;
    },
    restart: async (id, password) => {
        if (isDemo()) return mockMachineService.restart(id, password);
        const response = await apiClient.post(`/machines/${id}/restart`, { password });
        return response.data;
    },
    getScreenshot: async (id) => {
        if (isDemo()) return mockMachineService.getScreenshot(id);
        const response = await apiClient.get(`/machines/${id}/screenshot`, { responseType: 'blob' });
        return response.data;
    },
    ping: async (id) => {
        if (isDemo()) return mockMachineService.ping(id);
        const response = await apiClient.post(`/machines/${id}/ping`);
        return response.data;
    },
    clusterAction: async (machineIds, action) => {
        if (isDemo()) return mockMachineService.clusterAction(machineIds, action);
        const response = await apiClient.post('/machines/cluster-action', {
            machineIds,
            action,
            initiated_by: 'admin'
        });
        return response.data;
    },
    startKiosk: async (id, url, password) => {
        if (isDemo()) return mockMachineService.startKiosk(id, url, password);
        const response = await apiClient.post(`/machines/${id}/kiosk/start`, { url, password });
        return response.data;
    },
    stopKiosk: async (id, password) => {
        if (isDemo()) return mockMachineService.stopKiosk(id, password);
        const response = await apiClient.post(`/machines/${id}/kiosk/stop`, { password });
        return response.data;
    },
    bulkKiosk: async (machineIds, action, url, password) => {
        if (isDemo()) return mockMachineService.bulkKiosk(machineIds, action, url, password);
        const response = await apiClient.post('/machines/kiosk/bulk', {
            machineIds,
            action,
            url,
            password
        });
        return response.data;
    },
    downloadClientZip: async () => {
        if (isDemo()) {
            return new Blob(["Demo: Client Service Zip Archive"], { type: 'application/zip' });
        }
        const response = await apiClient.get('/machines/client-service/download', { responseType: 'blob' });
        return response.data;
    }
};

export const clusterService = {
    getAll: async () => {
        if (isDemo()) return mockClusterService.getAll();
        const response = await apiClient.get('/clusters');
        return response.data;
    },
    getById: async (id) => {
        if (isDemo()) return mockClusterService.getById(id);
        const response = await apiClient.get(`/clusters/${id}`);
        return response.data;
    },
    create: async (clusterData) => {
        if (isDemo()) return mockClusterService.create(clusterData);
        const response = await apiClient.post('/clusters', clusterData);
        return response.data;
    },
    update: async (id, clusterData) => {
        if (isDemo()) return mockClusterService.update(id, clusterData);
        const response = await apiClient.put(`/clusters/${id}`, clusterData);
        return response.data;
    },
    delete: async (id) => {
        if (isDemo()) return mockClusterService.delete(id);
        const response = await apiClient.delete(`/clusters/${id}`);
        return response.data;
    },
    performAction: async (id, action, password) => {
        if (isDemo()) return mockClusterService.performAction(id, action, password);
        const response = await apiClient.post(`/clusters/${id}/action`, {
            action,
            password,
            initiated_by: 'admin'
        });
        return response.data;
    }
};

export const logService = {
    getPowerLogs: async (limit = 100) => {
        if (isDemo()) return mockLogService.getPowerLogs(limit);
        const response = await apiClient.get(`/logs/power?limit=${limit}`);
        return response.data;
    },
    getSystemLogs: async (limit = 100) => {
        if (isDemo()) return mockLogService.getSystemLogs(limit);
        const response = await apiClient.get(`/logs/system?limit=${limit}`);
        return response.data;
    }
};

export const userService = {
    getAll: async () => {
        if (isDemo()) return mockUserService.getAll();
        const response = await apiClient.get('/users');
        return response.data;
    },
    create: async (userData) => {
        if (isDemo()) return mockUserService.create(userData);
        const response = await apiClient.post('/users', userData);
        return response.data;
    },
    update: async (id, userData) => {
        if (isDemo()) return mockUserService.update(id, userData);
        const response = await apiClient.put(`/users/${id}`, userData);
        return response.data;
    },
    delete: async (id) => {
        if (isDemo()) return mockUserService.delete(id);
        const response = await apiClient.delete(`/users/${id}`);
        return response.data;
    }
};

export default apiClient;
